import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Paper, Typography, TextField, Grid, Button, MenuItem, Autocomplete, Checkbox,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Chip, Divider, Tooltip,
} from "@mui/material";
import api from "../services/api";
import { todayISO } from "../utils/date";

const today = () => todayISO();
const fmt = (n) => (Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function Commission() {
  const [accounts, setAccounts] = useState([]);
  const [rows, setRows] = useState([]);

  const [agent, setAgent] = useState("");
  const [supplier, setSupplier] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [lbs, setLbs] = useState("");
  const [rate, setRate] = useState("");
  const [method, setMethod] = useState("Cash");
  const [checkNumber, setCheckNumber] = useState("");
  const [payDate, setPayDate] = useState(today());
  const [notes, setNotes] = useState("");
  const [computing, setComputing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [invoices, setInvoices] = useState([]);     // supplier's purchase invoices
  const [picked, setPicked] = useState(new Set());  // selected Master_IDs

  const amount = +(((Number(lbs) || 0) * (Number(rate) || 0)).toFixed(2));

  const loadAccounts = useCallback(() => {
    api.get("/accounts").then((r) => setAccounts(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);
  const loadList = useCallback(() => {
    api.get("/commission").then((r) => setRows(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  useEffect(() => { loadAccounts(); loadList(); }, [loadAccounts, loadList]);

  const names = accounts.map((a) => a.SubAccount_Name).filter(Boolean);

  const loadInvoices = async () => {
    if (!supplier) { alert("Pick the supplier you bought from first."); return; }
    try {
      setComputing(true);
      const r = await api.get("/commission/supplierInvoices", { params: { supplier, from: from || undefined, to: to || undefined } });
      setInvoices(Array.isArray(r.data) ? r.data : []);
      setPicked(new Set());
      setLbs("");
    } catch (e) {
      alert("Failed to load invoices: " + (e.response?.data?.error || e.message));
    } finally {
      setComputing(false);
    }
  };

  // Recompute the total lb from the ticked invoices whenever the selection changes.
  const applySelection = (next) => {
    setPicked(next);
    const sum = invoices.filter((iv) => next.has(iv.Master_ID))
      .reduce((s, iv) => s + (Number(iv.NetLbs) || 0), 0);
    setLbs(sum ? sum.toFixed(2) : "");
  };
  const toggleInvoice = (id) => {
    const next = new Set(picked);
    next.has(id) ? next.delete(id) : next.add(id);
    applySelection(next);
  };
  const toggleAll = () => {
    if (picked.size === invoices.length) applySelection(new Set());
    else applySelection(new Set(invoices.map((iv) => iv.Master_ID)));
  };
  const invoiceRefs = invoices.filter((iv) => picked.has(iv.Master_ID))
    .map((iv) => "#" + iv.Master_ID).join(", ");

  const save = async () => {
    if (!agent) { alert("Agent is required."); return; }
    if (!(amount > 0)) { alert("Amount must be greater than zero (set weight and rate)."); return; }
    try {
      setSaving(true);
      await api.post("/commission", {
        AgentName: agent, SupplierName: supplier || null,
        FromDate: from || null, ToDate: to || null,
        TotalLbs: Number(lbs) || null, Rate: Number(rate) || null,
        Amount: amount, Method: method,
        CheckNumber: (method === "Check" || method === "ACH") ? (checkNumber.trim() || null) : null,
        PaymentDate: payDate, Notes: notes || null,
        InvoiceRefs: invoiceRefs || null,
      });
      // reset money fields, keep agent/supplier for convenience
      setLbs(""); setRate(""); setCheckNumber(""); setNotes("");
      setInvoices([]); setPicked(new Set());
      loadList();
      alert("Commission recorded.");
    } catch (e) {
      alert("Failed to save: " + (e.response?.data?.error || e.message));
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this commission payment?")) return;
    try {
      await api.delete(`/commission/${id}`);
      loadList();
    } catch (e) {
      alert("Delete failed: " + (e.response?.data?.error || e.message));
    }
  };

  const totalPaid = rows.reduce((s, r) => s + (Number(r.Amount) || 0), 0);

  return (
    <Box p={2}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>Commissions</Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Pay a commission</Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          A referral fee to the person/company who brought you a supplier. It's recorded as an
          expense — it does <b>not</b> add inventory and never shows the agent as an owed account.
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Autocomplete freeSolo options={names} value={agent}
              onChange={(e, v) => setAgent(v || "")} onInputChange={(e, v) => setAgent(v || "")}
              renderInput={(p) => <TextField {...p} label="Agent (who gets paid)" fullWidth />} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Autocomplete freeSolo options={names} value={supplier}
              onChange={(e, v) => setSupplier(v || "")} onInputChange={(e, v) => setSupplier(v || "")}
              renderInput={(p) => <TextField {...p} label="Supplier you bought from" fullWidth />} />
          </Grid>

          <Grid size={{ xs: 6, sm: 3 }}>
            <TextField type="date" label="From" fullWidth InputLabelProps={{ shrink: true }}
              value={from} onChange={(e) => setFrom(e.target.value)} />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <TextField type="date" label="To" fullWidth InputLabelProps={{ shrink: true }}
              value={to} onChange={(e) => setTo(e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <Button variant="outlined" onClick={loadInvoices} disabled={computing} sx={{ height: 56 }} fullWidth>
              {computing ? "Loading…" : "Load invoices"}
            </Button>
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField type="number" label="Total lb (from ticked)" fullWidth value={lbs}
              onChange={(e) => setLbs(e.target.value)} helperText="Tick invoices below, or type" />
          </Grid>

          {invoices.length > 0 && (
            <Grid size={12}>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                Tick the invoices this commission is for — only the ticked ones are summed.
              </Typography>
              <TableContainer sx={{ maxHeight: 240, border: 1, borderColor: "divider", borderRadius: 1, mt: 0.5 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox size="small"
                          checked={picked.size === invoices.length && invoices.length > 0}
                          indeterminate={picked.size > 0 && picked.size < invoices.length}
                          onChange={toggleAll} />
                      </TableCell>
                      <TableCell>Invoice</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>PO</TableCell>
                      <TableCell>Items</TableCell>
                      <TableCell align="right">Net lb</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoices.map((iv) => (
                      <TableRow key={iv.Master_ID} hover selected={picked.has(iv.Master_ID)}
                        onClick={() => toggleInvoice(iv.Master_ID)} sx={{ cursor: "pointer" }}>
                        <TableCell padding="checkbox">
                          <Checkbox size="small" checked={picked.has(iv.Master_ID)} onChange={() => toggleInvoice(iv.Master_ID)} />
                        </TableCell>
                        <TableCell>#{iv.Master_ID}</TableCell>
                        <TableCell>{iv.Date ? String(iv.Date).slice(0, 10) : "-"}</TableCell>
                        <TableCell>{iv.PO || "-"}</TableCell>
                        <TableCell sx={{ maxWidth: 240, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          <Tooltip title={iv.Items || ""}><span>{iv.Items || "-"}</span></Tooltip>
                        </TableCell>
                        <TableCell align="right">{fmt(iv.NetLbs)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {picked.size > 0 && (
                <Typography variant="caption" sx={{ color: "primary.main", mt: 0.5, display: "block" }}>
                  {picked.size} invoice(s) selected · {invoiceRefs}
                </Typography>
              )}
            </Grid>
          )}

          <Grid size={{ xs: 6, sm: 3 }}>
            <TextField type="number" label="Rate per lb" fullWidth value={rate}
              onChange={(e) => setRate(e.target.value)} placeholder="0.01" />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <TextField label="Commission amount" fullWidth value={fmt(amount)} InputProps={{ readOnly: true }} />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <TextField select label="Method" fullWidth value={method} onChange={(e) => setMethod(e.target.value)}>
              <MenuItem value="Cash">Cash</MenuItem>
              <MenuItem value="Check">Check</MenuItem>
              <MenuItem value="ACH">ACH</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <TextField type="date" label="Payment date" fullWidth InputLabelProps={{ shrink: true }}
              value={payDate} onChange={(e) => setPayDate(e.target.value)} />
          </Grid>

          {(method === "Check" || method === "ACH") && (
            <Grid size={{ xs: 6, sm: 3 }}>
              <TextField label={method === "ACH" ? "ACH Ref #" : "Check #"} fullWidth
                value={checkNumber} onChange={(e) => setCheckNumber(e.target.value)} />
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: method === "Cash" ? 12 : 9 }}>
            <TextField label="Notes (optional)" fullWidth value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Grid>
        </Grid>

        <Box sx={{ mt: 2 }}>
          <Button variant="contained" onClick={save} disabled={saving}>
            {saving ? "Saving…" : `Pay commission $${fmt(amount)}`}
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, flexGrow: 1 }}>Commissions paid</Typography>
          <Chip color="primary" label={`Total: $${fmt(totalPaid)}`} />
        </Box>
        <Divider sx={{ mb: 1 }} />
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Agent</TableCell>
                <TableCell>Supplier</TableCell>
                <TableCell align="right">Lb</TableCell>
                <TableCell align="right">Rate</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Method</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.Commission_ID} hover>
                  <TableCell>{r.PaymentDate ? String(r.PaymentDate).slice(0, 10) : "-"}</TableCell>
                  <TableCell>{r.AgentName}</TableCell>
                  <TableCell>
                    {r.SupplierName || "-"}
                    {r.InvoiceRefs ? (
                      <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>{r.InvoiceRefs}</Typography>
                    ) : null}
                  </TableCell>
                  <TableCell align="right">{r.TotalLbs ? fmt(r.TotalLbs) : "-"}</TableCell>
                  <TableCell align="right">{r.Rate ?? "-"}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>${fmt(r.Amount)}</TableCell>
                  <TableCell>{r.Method}{r.CheckNumber ? ` ${r.CheckNumber}` : ""}</TableCell>
                  <TableCell align="right">
                    <Button size="small" color="error" onClick={() => del(r.Commission_ID)}>Delete</Button>
                  </TableCell>
                </TableRow>
              ))}
              {!rows.length && (
                <TableRow><TableCell colSpan={8} align="center" sx={{ color: "text.secondary" }}>No commissions yet</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
